import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchProductById, updateProduct } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { toast } from 'react-toastify';

const categories = ['Apparel', 'Appliances', 'Analog Watches', 'Backpacks', 'Footwear', 'Gift Items', 'Handbags', 'Helmets', 'Smart Watches', 'Speakers', 'Sports', 'Yoga'];

const EditProductPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        mrp: '',
        super_price: '',
        category: '',
        tags: '',
        productURL: '',
    });
    const [images, setImages] = useState(['']); // Array of image URLs

    useEffect(() => {
        loadProduct();
    }, [id]);

    const loadProduct = async () => {
        try {
            const response = await fetchProductById(id);
            const product = response.data;
            setFormData({
                name: product.name,
                description: product.description,
                mrp: product.mrp,
                super_price: product.super_price,
                category: product.category,
                tags: product.tags ? product.tags.join(', ') : '',
                productURL: product.productURL || ''
            });
            
            // Load images array, fallback to imageUrl for backward compatibility
            if (product.images && product.images.length > 0) {
                setImages(product.images);
            } else if (product.imageUrl) {
                setImages([product.imageUrl]);
            } else {
                setImages(['']);
            }
        } catch (err) {
            setError('Failed to load product');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (index, value) => {
        const newImages = [...images];
        newImages[index] = value;
        setImages(newImages);
    };

    const addImageField = () => {
        setImages([...images, '']);
    };

    const removeImageField = (index) => {
        if (images.length > 1) {
            const newImages = images.filter((_, i) => i !== index);
            setImages(newImages);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('You must be logged in to update a product.');

            // Filter out empty image URLs (images are now optional)
            const validImages = images.filter(url => url.trim() !== '');

            const productData = {
                ...formData,
                mrp: parseFloat(formData.mrp),
                super_price: parseFloat(formData.super_price),
                tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
                images: validImages.length > 0 ? validImages : []
            };

            await updateProduct(id, productData, token);
            toast.success('Product updated successfully');
            navigate('/admin/products');
        } catch (err) {
            setError(err.message || 'Failed to update product');
            toast.error('Failed to update product');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="min-h-screen bg-gray-100 p-8">
            <div className="max-w-4xl mx-auto">
                <div className="flex justify-between mb-6">
                    <button
                        onClick={() => navigate('/admin/products')}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                        Back to Products
                    </button>
                </div>

                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="px-6 py-8 sm:p-10">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold text-gray-900">Edit Product</h2>
                        </div>

                        {error && <ErrorMessage message={error} />}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Product Name</label>
                                        <input
                                            id="name"
                                            name="name"
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="Product Name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Description</label>
                                        <textarea
                                            id="description"
                                            name="description"
                                            required
                                            value={formData.description}
                                            onChange={handleChange}
                                            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="Product Description"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Category</label>
                                        <select
                                            id="category"
                                            name="category"
                                            required
                                            value={formData.category}
                                            onChange={handleChange}
                                            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        >
                                            <option value="">Select Category</option>
                                            {categories.map((category, index) => (
                                                <option key={index} value={category}>{category}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Tags (comma-separated, optional)</label>
                                        <input
                                            id="tags"
                                            name="tags"
                                            type="text"
                                            value={formData.tags}
                                            onChange={handleChange}
                                            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="e.g., new, sale, featured"
                                        />
                                        <p className="mt-1 text-sm text-gray-500">
                                            Optional: Add tags separated by commas
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">MRP (₹)</label>
                                        <input
                                            id="mrp"
                                            name="mrp"
                                            type="number"
                                            step="0.01"
                                            required
                                            value={formData.mrp}
                                            onChange={handleChange}
                                            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="MRP"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Super Price (₹)</label>
                                        <input
                                            id="super_price"
                                            name="super_price"
                                            type="number"
                                            step="0.01"
                                            required
                                            value={formData.super_price}
                                            onChange={handleChange}
                                            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="Super Price"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Image URLs
                                        </label>
                                        {images.map((imageUrl, index) => (
                                            <div key={index} className="mb-4">
                                                <div className="flex gap-2">
                                                    <input
                                                        type="url"
                                                        value={imageUrl}
                                                        onChange={(e) => handleImageChange(index, e.target.value)}
                                                        className="flex-1 appearance-none rounded-md relative block px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                        placeholder={`Image URL ${index + 1}`}
                                                    />
                                                    {images.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeImageField(index)}
                                                            className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                                {imageUrl && (
                                                    <div className="mt-2">
                                                        <img 
                                                            src={imageUrl} 
                                                            alt={`Preview ${index + 1}`} 
                                                            className="w-full h-48 object-cover rounded-md border border-gray-200"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={addImageField}
                                            className="mt-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                        >
                                            + Add Another Image
                                        </button>
                                        <p className="mt-2 text-sm text-gray-500">
                                            Add multiple image URLs for the product (optional)
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Product URL (Internal - Not Displayed)</label>
                                        <input
                                            id="productURL"
                                            name="productURL"
                                            type="url"
                                            value={formData.productURL}
                                            onChange={handleChange}
                                            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="Product Source URL (e.g., Flipkart, Amazon link)"
                                        />
                                        <p className="mt-1 text-sm text-gray-500">
                                            This URL is stored for internal reference and will not be displayed to users
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-smooth"
                                    disabled={loading}
                                >
                                    {loading ? <LoadingSpinner /> : 'Update Product'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditProductPage;
